/**
 * StaffFormModal — create or edit a staff member.
 *
 * Mirrors StudentFormModal's react-hook-form + backend-422-mapping pattern.
 * Password/auth_method fields only apply on create (provisioning); edit only
 * updates the Firestore-backed profile fields.
 */

import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Modal, Button, Input } from '@/components/common';
import {
  useCreateStaff,
  useUpdateStaff,
  type StaffCreate,
  type StaffUpdate,
  type StaffResponse,
} from '@features/people/api/staff';
import type { ApiError } from '@core/api/client';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-family: inherit;
  resize: vertical;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  margin-top: 2px;
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  staff?: StaffResponse;
}

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone: string;
  role: string;
  staff_type: string;
  hire_date: string;
  bio: string;
  expertise: string; // comma-separated in the UI, split on submit
}

const EMPTY: FormValues = {
  first_name: '', last_name: '', email: '', password: '', phone: '',
  role: 'staff', staff_type: '', hire_date: '', bio: '', expertise: '',
};

export const StaffFormModal: React.FC<Props> = ({ isOpen, onClose, staff }) => {
  const isEdit = Boolean(staff);
  const createMut = useCreateStaff();
  const updateMut = useUpdateStaff();

  const {
    register, handleSubmit, reset, setError, control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: EMPTY });

  useEffect(() => {
    if (isOpen) {
      reset(
        staff
          ? {
              first_name: staff.first_name,
              last_name: staff.last_name,
              email: staff.email,
              password: '',
              phone: staff.phone ?? '',
              role: staff.role,
              staff_type: staff.staff_type ?? '',
              hire_date: staff.hire_date ?? '',
              bio: staff.bio ?? '',
              expertise: (staff.expertise ?? []).join(', '),
            }
          : EMPTY,
      );
    }
  }, [isOpen, staff, reset]);

  const handleClose = () => { reset(EMPTY); onClose(); };

  const onSubmit = handleSubmit(async (values) => {
    const expertiseList = values.expertise
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      if (isEdit && staff) {
        const body: StaffUpdate = {
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone || null,
          role: values.role as StaffUpdate['role'],
          staff_type: values.staff_type || null,
          hire_date: values.hire_date || null,
          bio: values.bio || null,
          expertise: expertiseList,
        };
        await updateMut.mutateAsync({ id: staff.id, body });
        toast.success('Staff member updated');
      } else {
        const body: StaffCreate = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          phone: values.phone || null,
          role: values.role as StaffCreate['role'],
          staff_type: values.staff_type || null,
          hire_date: values.hire_date || null,
          bio: values.bio || null,
          expertise: expertiseList,
        };
        await createMut.mutateAsync(body);
        toast.success('Staff member created');
      }
      handleClose();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 422 && Array.isArray(apiErr.details)) {
        (apiErr.details as { loc: (string | number)[]; msg: string }[]).forEach(({ loc, msg }) => {
          const field = loc[loc.length - 1] as keyof FormValues;
          if (field) setError(field, { type: 'server', message: msg });
        });
      } else {
        toast.error(apiErr.message || 'Failed to save staff member');
      }
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit Staff Member' : 'Add New Staff Member'}
      size="lg"
      data-testid="candeza-staff-form-modal"
    >
      <form onSubmit={onSubmit} noValidate>
        <FormGrid>
          <div>
            <Input label="First Name *" placeholder="First name" {...register('first_name', { required: 'Required' })} data-testid="candeza-staff-form-first-name-input" />
            {errors.first_name && <ErrorText>{errors.first_name.message}</ErrorText>}
          </div>
          <div>
            <Input label="Last Name *" placeholder="Last name" {...register('last_name', { required: 'Required' })} data-testid="candeza-staff-form-last-name-input" />
            {errors.last_name && <ErrorText>{errors.last_name.message}</ErrorText>}
          </div>
          <div>
            <Input
              label="Email *"
              type="email"
              placeholder="staff@candeza.co"
              disabled={isEdit}
              {...register('email', { required: 'Required' })}
              data-testid="candeza-staff-form-email-input"
            />
            {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
          </div>
          <div>
            <Input label="Phone" type="tel" placeholder="+91 98765 43210" {...register('phone')} data-testid="candeza-staff-form-phone-input" />
          </div>
          {!isEdit && (
            <div>
              <Input label="Password *" type="password" placeholder="Min 8 characters" {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })} data-testid="candeza-staff-form-password-input" />
              {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
            </div>
          )}
          <div>
            <FieldLabel>Role</FieldLabel>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <select
                  {...field}
                  data-testid="candeza-staff-form-role-select"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', fontSize: '14px' }}
                >
                  <option value="staff">Staff / Instructor</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              )}
            />
          </div>
          <div>
            <Input
              label="Staff type"
              placeholder="e.g. instructor, admin_staff, part_time"
              {...register('staff_type')}
              data-testid="candeza-staff-form-staff-type-input"
            />
          </div>
          <div>
            <Input label="Hire Date" type="date" {...register('hire_date')} data-testid="candeza-staff-form-hire-date-input" />
          </div>
          <FullWidth>
            <Input
              label="Expertise (comma-separated)"
              placeholder="e.g. Guitar, Music Theory, Ear Training"
              {...register('expertise')}
              data-testid="candeza-staff-form-expertise-input"
            />
          </FullWidth>
          <FullWidth>
            <FieldLabel>About / bio</FieldLabel>
            <TextArea
              placeholder="Shown on the Overview tab of the Staff detail page"
              {...register('bio')}
              data-testid="candeza-staff-form-bio-input"
            />
          </FullWidth>
        </FormGrid>
        <ModalActions>
          <Button type="button" variant="outline" onClick={handleClose} data-testid="candeza-staff-form-cancel-btn">Cancel</Button>
          <Button type="submit" disabled={isSubmitting} data-testid="candeza-staff-form-save-btn">
            {isSubmitting ? 'Saving...' : isEdit ? 'Save changes' : 'Create Staff'}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};

